import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import { useNavigate } from "react-router-dom";
import { object, string } from "yup";
import { useFormik } from "formik";
import { useSelector } from "react-redux";
import {
  callAPIInterface,
  getTimestamp,
  handleAfterLoginProcess,
  isTMA,
  setUpdatedSessionData,
} from "../constants";
import {
  emailAddress,
  emailAddressPlaceholder,
  forgotPassword,
  loadingMsg,
  password,
  passwordPlaceHolder,
  signIn,
  signInHeader,
  signInSubHeader,
  signUp,
} from "../messages";
import { CustomLink } from "../link/Link";
import { Input } from "../input/Input";
import BackdropLoader from "../backdropLoader/BackdropLoader";
import Text from "../text/Text";
import Button from "../button/Button";
import CommonAuthHeader from "../options/CommonAuthHeader";
import CommonHeader from "../CommonHeader";

const LoginStepTwo = ({ isFromOptions = false }) => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const loginDetails = useSelector((state) => state.common.loginDetails);

  const telegramAppSx = {
    backgroundColor: "white",
  };

  const webAppSx = {
    height: "100%",
  };

  useEffect(() => {
    if (!loginDetails?.email) {
      navigate(-1);
    }
  }, []);

  const validationSchema = object({
    password: string().required(),
  });

  const handleLogin = async (val) => {
    setIsSubmitting(true);
    try {
      const data = { ...loginDetails, ...val };
      const response = await callAPIInterface(
        "POST",
        "/login",
        JSON.stringify(data)
      );
      response.timestamp = getTimestamp();
      await setUpdatedSessionData(response, data);

      const sessionState = response?.session_state;

      if (sessionState === "mfa_in_progress") {
        const redirect2faPage = response?.mfa_configurations
          ? "/two-factor"
          : "/two-factor-setup";
        navigate(redirect2faPage, {
          state: {
            from: "/login",
          },
        });
      } else if (sessionState === "trusted_device_in_progress") {
        navigate("/unrecognized-device");
      } else {
        await handleAfterLoginProcess(navigate);
      }
    } catch (error) {
    } finally {
      setIsSubmitting(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      password: "",
    },
    validationSchema: validationSchema,
    onSubmit: handleLogin,
  });

  const {
    values,
    touched,
    errors,
    isValid,
    dirty,
    handleChange,
    handleSubmit,
    handleBlur,
  } = formik;

  const signUpComponent = (
    <Text
      className="blue-text"
      font="bold"
      size={16}
      onClick={() => navigate("/register")}
      sx={{ cursor: "pointer" }}
    >
      {signUp}
    </Text>
  );
  return (
    <>
      <BackdropLoader
        open={isSubmitting}
        alt={loadingMsg}
        text={loadingMsg}
        customClass="loading-in-auth"
      />
      <CommonHeader
        icon="backArrow"
        rightSideComponent={signUpComponent}
        navigation="/"
      />
      <CommonAuthHeader
        showSpeedLogo={false}
        sx={isTMA() ? telegramAppSx : webAppSx}
      >
        <Box
          component="form"
          className="set-elements-vertically"
          onSubmit={handleSubmit}
        >
          <Box>
            <Text className="default-text" size={22} font="bold">
              {signInHeader}
            </Text>
            <Text className="grey-text mt-8" size={14}>
              {signInSubHeader}
            </Text>
            <Input
              customClass="mt-24"
              fullWidth
              showLabel
              label={emailAddress}
              placeholder={emailAddressPlaceholder}
              name="email"
              value={loginDetails?.email}
              type="email"
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={true}
            />
            <Input
              customClass="mt-16"
              fullWidth
              type="password"
              isPasswordVisible={true}
              showLabel
              label={password}
              placeholder={passwordPlaceHolder}
              name="password"
              value={values.password}
              onChange={handleChange}
              error={touched.password && Boolean(errors.password)}
            />
            <CustomLink
              sx={{ float: "right", mt: 2, mb: 3 }}
              bold
              onClick={() =>
                navigate("/forgot-password", {
                  state: { isFromLoginScreen: true },
                })
              }
            >
              {forgotPassword}
            </CustomLink>
          </Box>
          <Box mt="10%">
            <Button
              fullWidth
              sx={!isFromOptions && { m: "14px 20px" }}
              variant="contained"
              label={signIn}
              type="submit"
              disabled={!(isValid && dirty)}
              {...(!isFromOptions && { textClass: "font-14" })}
            />
          </Box>
        </Box>
      </CommonAuthHeader>
    </>
  );
};

export default LoginStepTwo;
